import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import useBulkWorkspaces from "../../../hooks/useBulkWorkspaces";
import useWorkspaces from '../../../hooks/useWorkspaces';
import { scrollbarStyle } from '../../../shared/dashboard';
import { Dashboard, Workspace, WorkspaceScore, WorkspaceScoreItem } from '../../../types/files';
import { getUniqueElements } from '../../../utils/getUniqueElements';
import NoWorkspacesFoundView from '../../molecules/workspaces-empty/NoWorkspacesFoundView';
import WorkspacesSearchOrCreate from '../../molecules/workspaces-search-create/WorkspacesSearchOrCreate';
import WorkspacesTableHeader from '../../molecules/workspaces-table/WorkspacesTableHeader';
import WorkspacesTableRow from "../../molecules/workspaces-table/WorkspacesTableRow";
import { FallbackLoading } from "../../templates/loader";

const useStyles = makeStyles((theme) => ({
    container: {
        height: '100%',
        overflow: 'hidden',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        color: '#666666',
    },
    table: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        ...scrollbarStyle,
    },
    rows: {
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    selected :{
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: 'black',
    },
    copyButton: {
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.main,
            }
        },
    },
}));

export const WorkspaceSelectionKey = 'workspaces:select';
const TableHeaders = ['Name', 'Fit score', '[2]'];

const FoundInvestors: React.FC<{ dashboards: Dashboard[], collectionKey: string, }> = ({ dashboards, collectionKey }) => {
    const classes = useStyles();
    const { workspaces, workspaceScores, setWorkspaceScores } = useContext(WorkspacesContext);
    const { getBulkWorkspaces, clearBulk } = useBulkWorkspaces();
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();

    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('Added');
    const [investors, setInvestors] = useState<Workspace[]>([]);
    const [scoring, setScoring] = useState<boolean>(false);

    const timerRefA = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const timerRefB = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const dashboardIds = useMemo(() => dashboards.map(({id}) => id), [dashboards]);
    const bulkWorkspaces = useMemo(() => getBulkWorkspaces(collectionKey), [getBulkWorkspaces, collectionKey]);

    const dashboard = useMemo(() => {
        if (collectionKey === 'workspaces:select') {
            const dashboardId = dashboardIds.sort().join('+');
            const newDashboard: Dashboard = {
                id: dashboardId,
                title: '<avg.investors.score>',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            } as unknown as Dashboard;

            return newDashboard;
        }

        const match = collectionKey.match(/dashboard\[(.*?)\]/);
        const id = match?.[1];

        return dashboards.find(dashboard => dashboard.id === id);
    }, [collectionKey, dashboardIds, dashboards]);

    const workspaceScoreMap: Map<Workspace, number> = useMemo(() => new Map(
        workspaces.map(workspace => [workspace,
            workspaceScores.find(({workspaceId}) => workspaceId === workspace.id)?.scores
                ?.find(({dashboardId}) => dashboardIds.includes(dashboardId || ''))?.score || 0
        ]))
    , [dashboardIds, workspaceScores, workspaces]);

    const sortedWorkspaces = useMemo(() => {
        if (sortBy.includes('Fit score'))
            return Array.from(workspaceScoreMap)
                .sort((a, b) => a[0].name.localeCompare(b[0].name))
                .sort((a, b) => b[1] - a[1]).map(entry => entry[0]);

        return [...workspaces ?? []].sort((a: Workspace, b: Workspace) =>
            (b.createdAt?.toString() ?? '').localeCompare(a.createdAt?.toString() ?? '')
        );
    }, [sortBy, workspaceScoreMap, workspaces]);

    const searchWorkspaces = useMemo(() => {
        const searchText = (search ?? '').trim().toLowerCase();
        const keywords = searchText.split(',').map(token => token.trim());
        let filtered: Workspace[] = sortedWorkspaces;

        if (keywords.some(keyword => keyword.length > 0)) {
            if (keywords.length === 1) {
                // if keyword is only one, search in name, email, and website
                filtered = filtered.filter(workspace =>
                    workspace.name?.toLowerCase().includes(searchText) ||
                    workspace.email?.toLowerCase().includes(searchText) ||
                    workspace.website?.toLowerCase().includes(searchText)
                );
            } else {
                // match keywords in sector, stage, geography
                filtered = filtered.filter(workspace => {
                    const tags = workspace.investmentThesis?.focusArea?.tags?.map(sector =>sector.toLowerCase()) || [];
                    const stages = workspace.investmentThesis?.stage?.stages?.map(stage =>stage.toLowerCase()) || [];
                    const regions = workspace.investmentThesis?.geography?.regions?.map(geo =>geo.toLowerCase()) || [];

                    return keywords.map(key =>
                        [...tags, ...stages, ...regions].map(word => word.includes(key) ? key : null)
                    ).flat().filter(getUniqueElements).filter(Boolean)[0];
                });
            }
        }

        return filtered;
    }, [search, sortedWorkspaces]);

    const filteredWorkspaces = useMemo(() =>
        searchWorkspaces.filter(workspace => !workspace.isShared)
    , [searchWorkspaces]);

    const averageWorkspaceScores = useCallback((updatedWorkspaceScores: WorkspaceScore[]) =>
        updatedWorkspaceScores.map(workspaceScores => {
            const newScores: WorkspaceScoreItem[] = [{
                dashboardId: dashboard?.id,
                score: dashboards.reduce((sumScores, dashboard) =>
                    sumScores += workspaceScores?.scores?.find(wsScore => wsScore.dashboardId === dashboard?.id)?.score || 0
                , 0) / (dashboards?.length || 1),
            }];
            const mergedScores: WorkspaceScoreItem[] = [...workspaceScores.scores ?? []];

            newScores.forEach(newScore => {
                const existingScoreIndex = mergedScores.findIndex(score => score.dashboardId === newScore.dashboardId);

                if (existingScoreIndex !== -1)
                mergedScores[existingScoreIndex] = newScore;
                else
                mergedScores.push(newScore);
            });

            return { ...workspaceScores, scores: mergedScores }
    }), [dashboard?.id, dashboards]);

    const handleRefresh = useCallback(() =>
        refreshScores(workspaces, dashboards, () => setScoring(true))
    , [refreshScores, dashboards, workspaces]);

    const handleComputeScores = useCallback(() => {
        computeWorkspaceScores(workspaces).then((updatedWorkspaceScores) => {
            if (dashboard?.title === '<avg.investors.score>')
                setWorkspaceScores(averageWorkspaceScores(updatedWorkspaceScores));
            else
               setWorkspaceScores(updatedWorkspaceScores);
       }).finally(() => setScoring(false));
    }, [computeWorkspaceScores, workspaces, dashboard?.title, setWorkspaceScores, averageWorkspaceScores]);

    const handleCopy = useCallback(() => {
        const emails = bulkWorkspaces.reduce((emails, ws) => {
            if (!!ws.email)
                emails.push(ws.email)

            return emails;
        }, [] as string[]).join(', ');

        navigator.clipboard.write([new ClipboardItem({
            'text/plain': new Blob([emails], { type: 'text/plain' }),
            'text/html': new Blob([emails], { type: 'text/html' }),
        })]);
    }, [bulkWorkspaces]);

    useEffect(() => {
        setScoring(false);

        return () => {
            clearBulk(WorkspaceSelectionKey);
            dashboards.forEach(dashboard => clearBulk(`dashboard[${dashboard.id}]`));
        }
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        timerRefA.current = setTimeout(() => {
            clearTimeout(timerRefA.current);
            setInvestors(filteredWorkspaces);
            setLoading(false);
        }, 500);

        return () => {
            clearTimeout(timerRefA.current);
        }
    // eslint-disable-next-line
    }, [filteredWorkspaces]);

    useEffect(() => {
        setLoading(!!timerRefA.current && !investors.length);
    }, [investors.length]);

    useEffect(() => {
        if (scoring || !loading) {
            timerRefB.current = setTimeout(() => {
                clearTimeout(timerRefB.current);
                handleComputeScores();
            }, 3000);
       }

       return () => {
           clearTimeout(timerRefB.current);
       }
    // eslint-disable-next-line
    }, [scoring]);

    return (<>
        <Stack direction="column" className={classes.container} alignItems="stretch" justifyContent="space-between">
            <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="flex-start" height="90%">
                <Typography className={classes.title}>
                    {`List of investors based on their deal preferences: `}
                </Typography>
                <Stack className={classes.table} >
                    <Stack className={classes.headers} alignItems="flex-start" justifyContent="flex-start">
                        <WorkspacesSearchOrCreate search mediumFit onSearch={(value) => setSearch(value)} />
                        {loading ? (<FallbackLoading />) : (<>
                            {!!investors.length ? (<>
                                <WorkspacesTableHeader
                                    columns={TableHeaders}
                                    onColumnSort={setSortBy}
                                    onRefreshScores={handleRefresh}
                                    findInvestor />
                                <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                    {investors.map(investor => (
                                        <WorkspacesTableRow
                                            dashboard={dashboard}
                                            workspace={investor}
                                            columns={TableHeaders}
                                            collectionKey={collectionKey}
                                            refreshingScore={scoring}
                                            noMenu withFitScore findInvestor
                                            key={'found-investors-214-' + investor.id} />
                                    ))}
                                </Stack>
                            </>) : (<>
                                <WorkspacesTableHeader columns={TableHeaders} findInvestor />
                                <NoWorkspacesFoundView noResult />
                            </>)}
                        </>)}
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="flex-end" height="10%">
                <Divider sx={{ width: '100%' }} />
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Typography className={classes.selected}>
                        {`${bulkWorkspaces.length} investor${bulkWorkspaces.length > 1 ? 's': ''} selected`}
                    </Typography>
                    {!loading && (
                        <Button className={classes.copyButton}
                            startIcon={<ContentCopyIcon />}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleCopy();
                            }}> {'Copy emails'} </Button>
                    )}
                </Stack>
            </Stack>
        </Stack>
    </>);
}
export default memo(FoundInvestors);